import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '@/components/admin/Sidebar';
import { ThemeProvider } from '@/context/ThemeContext';
import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

// Componente interno para acceder al contexto
const AdminLayoutContent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
            if (!mobile) setIsSidebarOpen(true);
            else setIsSidebarOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Validar sesión simple almacenada
        const sessionStr = localStorage.getItem('plasa_admin_session');
        if (!sessionStr) {
            navigate('/admin');
            return;
        }

        try {
            JSON.parse(sessionStr);
            setIsAuthenticated(true);
        } catch (e) {
            localStorage.removeItem('plasa_admin_session');
            navigate('/admin');
        }
    }, [navigate, location]);

    if (!isAuthenticated) return null;

    return (
        <div className="flex h-screen bg-gray-50 dark:bg-black text-zinc-900 dark:text-white overflow-hidden font-sans transition-colors duration-300">
            {/* Backdrop for mobile */}
            <AnimatePresence>
                {isMobile && isSidebarOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsSidebarOpen(false)}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 lg:hidden"
                    />
                )}
            </AnimatePresence>

            <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} isMobile={isMobile} />

            {/* Main Content Area */}
            <main className="flex-1 overflow-auto bg-gray-100 dark:bg-zinc-950 relative transition-colors duration-300 flex flex-col">
                {/* Mobile Header */}
                <header className="lg:hidden h-16 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 flex items-center justify-between px-4 sticky top-0 z-40 shrink-0">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setIsSidebarOpen(true)}
                            className="p-2 -ml-2 text-zinc-500 hover:text-red-600 transition-colors"
                        >
                            <Menu size={24} />
                        </button>
                        <span className="font-black text-lg tracking-tighter">PLASA<span className="text-red-600">.ADMIN</span></span>
                    </div>
                </header>

                <div className="flex-1 overflow-auto relative">
                    {/* Subtle Background Pattern */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,#00000000_70%,#00000000_100%)] dark:bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none"></div>

                    <div className="relative z-10 px-4 sm:px-8 py-6 sm:py-8 max-w-7xl mx-auto w-full">
                        <Outlet />
                    </div>
                </div>
            </main>
        </div>
    );
};

const AdminLayout = () => {
    return (
        <ThemeProvider>
            <AdminLayoutContent />
        </ThemeProvider>
    );
};

export default AdminLayout;
